import React, { useState, useEffect } from 'react';
import { doctorProfileAPI } from '../services/api';

/**
 * DocumentImagePreview
 *
 * Renders an inline thumbnail for a doctor document. Fetches the bytes
 * through the backend proxy (so JWT auth flows correctly), wraps them in
 * an object URL, and renders <img>. Falls back to a 📄 icon for PDFs.
 *
 * Cleans up the blob URL on unmount to avoid memory leaks.
 *
 * Props:
 *   doctorId   — string (UUID)
 *   docId      — string (UUID)
 *   mimeType   — string, e.g. 'image/png' or 'application/pdf'
 *   size       — number, thumbnail size in pixels (default 56)
 *   onClick    — optional, called when the thumbnail is clicked (e.g., to enlarge)
 */
export default function DocumentImagePreview({ doctorId, docId, mimeType, size = 56, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isImage = mimeType && mimeType.startsWith('image/');

  useEffect(() => {
    if (!isImage || !doctorId || !docId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let createdUrl = null;

    (async () => {
      try {
        const res = await doctorProfileAPI.getDocumentBlob(doctorId, docId);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data);
        setBlobUrl(createdUrl);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [doctorId, docId, isImage]);

  const containerStyle = {
    width: size,
    height: size,
    borderRadius: 6,
    border: '1px solid var(--border)',
    background: '#fafafa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    cursor: onClick ? 'pointer' : 'default',
    fontSize: Math.round(size * 0.4),
  };

  if (!isImage) {
    return (
      <div style={containerStyle} onClick={onClick} title="PDF">📄</div>
    );
  }

  if (loading) {
    return (
      <div style={containerStyle} title="Φόρτωση...">⏳</div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, color: 'var(--danger)' }} title="Αποτυχία φόρτωσης">⚠️</div>
    );
  }

  return (
    <div style={containerStyle} onClick={onClick}>
      <img
        src={blobUrl}
        alt="document preview"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
}
